import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, CheckboxAPI, FetchAPI, FieldAPI, FormAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

import { FragmentNames, BloodManagementReactionAliases as ReactionAliases } from '../../../../../codegen';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import MedicationService from '../../../services/medication-service';
import { Validator } from 'src/interfaces/spec-layout';
import { ReactionType } from '../blood-management-types';

const requiredValidator: Validator = {
  name: 'required',
  options: {
    runOnFocus: false,
    showWhenValid: false
  }
}

@Controller({ component: ReactionAliases.REACTION_LAYOUT, fragment: FragmentNames.BLOOD_MANAGEMENT_REACTION })
export default class BloodManagementReactionController extends BaseController {

  @Inject(ReactionAliases.FORM_REACTION)
  private formReactionApi!: FormAPI;

  @Inject(ReactionAliases.FIELD_SYMPTOMS)
  private fieldSymptomsApi!: FieldAPI;

  @Inject(ReactionAliases.FIELD_REASON)
  private fieldReasonApi!: FieldAPI;

  @Inject(ReactionAliases.TEXT_AREA_SYMPTOMS)
  private textAreaSymptomsApi!: TextAreaAPI;

  @Inject(ReactionAliases.AUTOCOMPLETE_REASON_LIST)
  private reasonAutocompleteApi!: AutocompleteAPI;

  @Inject(ReactionAliases.CHECKBOX_DENIES_REACTION)
  private deniesReactionCheckboxApi!: CheckboxAPI;

  @OnMount(ReactionAliases.REACTION_LAYOUT)
  handleMountLayou(): void {
    const { encounterId, patientId, productReserved } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId, productReserved });
    InformationBarUtils.collapsedBar = true;
  }

  @OnMount(ReactionAliases.FETCH_REASON)
  handleFetchReasonMount(event: MountEvent): void {
    const { itemType } = this.historyService.getCurrentRouteParams();
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: String(itemType) }, { skip: true });
  }

  @OnChange(ReactionAliases.CHECKBOX_DENIES_REACTION)
  handleCheckboxDeniesReactionChange({ value }: ChangeEvent<boolean>): void {
    this.textAreaSymptomsApi.setEnabled(!value);
    this.reasonAutocompleteApi.setEnabled(!value);
    if (value) {
      this.fieldReasonApi.removeValidator('required');
      this.reasonAutocompleteApi.setValue(undefined);
      this.fieldSymptomsApi.removeValidator('required');
      this.fieldSymptomsApi.setValue('');
      return;
    }
    this.fieldReasonApi.addValidator(requiredValidator);
    this.fieldSymptomsApi.addValidator(requiredValidator);
  }

  @OnChange(ReactionAliases.AUTOCOMPLETE_REASON_LIST)
  handleAutocompleteReasonChange({ value }: ChangeEvent<Record<string, Value>[]>): void {
    if (value) {
      this.textAreaSymptomsApi.setRequired(true);
    }
  }

  @OnBeforeCancel(ReactionAliases.FORM_REACTION)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(ReactionAliases.FORM_REACTION)
  async handleSubmitForm(): Promise<void> {
    const isValid = await this.formReactionApi.validate();
    if (isValid) {
      this.recordTransfusionReaction();
    }
  }

  private recordTransfusionReaction(): void {
    this.formReactionApi.setLocked(true);
    this.formReactionApi.setLoading(true);

    const body = this.getBody();

    HttpService.post(Endpoint.EMAR_BLOOD_RECORD_TRANSFUSION_REACTION, {}, { body })
      .then(() => {
        MedicationService.showToast(MessageService.translate('txt.reaction-registered-with-success'), ToastLevel.SUCCESS)
        this.goToTimeline();
      })
      .catch((error) => this.handleApiError(error))
      .finally(() => {
        this.formReactionApi.setLoading(false);
        this.formReactionApi.setLocked(false);
      });
  }

  private handleApiError(error: any): void {
    if (error.body && error.body.errors) {
      error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
    }
  }

  private getBody(): Record<string, Value> {
    const reactionSelected = this.reasonAutocompleteApi.getValue() as unknown as ReactionType;
    const deniesReaction = this.deniesReactionCheckboxApi.getValue();
    const { productReserved } = this.historyService.getCurrentRouteParams();

    return {
      deniesReaction: deniesReaction,
      productionCode: productReserved,
      reasonCode: reactionSelected?.reactionCode,
      symptoms: this.textAreaSymptomsApi.getValue(),
    } as Record<string, Value>;
  }

  private goToTimeline(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
      encounterId,
      patientId,
    });
    applicationContext.clearNavigationStack();
  }
}